import "./init";
import { store } from "./store/store";
import "./themes/index.css";
import EmptyStatus from "@components/TablePagination/EmptyStatus";
import { DEFAULT_LANGUAGE, LOCALSTORAGE_KEY, GOOGLE_CLIENT_ID, GOOGLE_CLIENT_ID_STAGING } from "@config";
import { reportKaloPageload } from "@report";
import { ConfigProvider } from "antd";
import ReactDOM from "react-dom/client";
import { Provider } from "react-redux";
import { BrowserRouter } from "react-router-dom";
import { GoogleOAuthProvider } from "@react-oauth/google";
import App from "./app/app";
import "@app/antdcss/index";
import { useEffect, useLayoutEffect, useState } from "react";
import { AntLanguage, Language } from "./config/language";
import { handle360AdQhclickid } from "@utils/util";
import { useAtom } from "jotai";
import { themeAtom, themeEffectAtom, antdThemeAtom } from "./jotai/theme";
import PubSub from "pubsub-js";
import * as Sentry from "@sentry/react";
import { ErrorBoundaryFallback } from "@components/ErrorBoundaryFallback/ErrorBoundaryFallback";

// 根据环境获取 Google Client ID
const isStaging = window.location.hostname.includes("staging");
const googleClientId = isStaging ? GOOGLE_CLIENT_ID_STAGING : GOOGLE_CLIENT_ID;


reportKaloPageload();

if (navigator.platform.toUpperCase().indexOf("MAC") < 0) {
  import("./browser.css");
}

const language = (localStorage.getItem(LOCALSTORAGE_KEY.LANGUAGE) || DEFAULT_LANGUAGE) as Language;



const urlSearchParams = new URLSearchParams(window.location.search.slice(1));
const region = urlSearchParams.get("region");
if (region) {
  localStorage.setItem(LOCALSTORAGE_KEY.REGION, region);
}

const ThemeProvider = ({ children }: { children: React.ReactNode }) => {
  // resolvedTheme（light/dark）由 themeAtom 派生，自动响应：
  // 1) 用户手动切换 themePreference（含跨标签 storage 事件）
  // 2) preference="system" 时系统 prefers-color-scheme 变化
  const [theme] = useAtom(themeAtom);
  const [, applyThemeEffect] = useAtom(themeEffectAtom);

  // 使用 useLayoutEffect 同步在浏览器绘制前更新 html.dark，避免内容区与侧栏不同帧切换
  useLayoutEffect(() => {
    applyThemeEffect(theme);
  }, [theme, applyThemeEffect]);

  return <>{children}</>;
};

// 暂时只对日语字体做处理
const languageMap: any = {
  // "zh-CN": "NotoSansSC",
  "ja-JP": "NotoSansJP",
  // "th-TH": "NotoSansThai",
}
const Root = () => {
  const [locale, setLocale] = useState(AntLanguage[language]);
  const [antdTheme] = useAtom(antdThemeAtom);
  const [_theme] = useAtom(themeAtom);
  
  useEffect(() => {
    // 上报360广告
    handle360AdQhclickid()
    PubSub.subscribe("LanguageChange", (_, language: string) => {
      // @ts-ignore
      setLocale(AntLanguage[language]);
    });
  }, []);
  const localeType = locale;
    
  // 根据语言设置字体，与原来的逻辑保持一致
  const finalTheme = languageMap[language]
    ? {
        ...antdTheme,
        token: {
          ...antdTheme.token,
          fontFamily: languageMap[language]
        }
      }
    : antdTheme;


  return (
    <Sentry.ErrorBoundary
      fallback={ErrorBoundaryFallback}
      showDialog
    >
    <GoogleOAuthProvider clientId={googleClientId}>
      <BrowserRouter>
        <Provider store={store}>
            <ThemeProvider>
              <ConfigProvider
                locale={localeType}
                renderEmpty={() => <EmptyStatus />}
                theme={finalTheme as any}
                modal={{
                  mask: {
                    blur: false, // 关闭 Modal 蒙层模糊效果（如需开启可设为 true 或删除此配置）
                  },
                }}
                drawer={{
                  mask: {
                    blur: false, // 关闭 Drawer 蒙层模糊效果（如需开启可设为 true 或删除此配置）
                  },
                }}
              >
                
                  <App />
              </ConfigProvider>
            </ThemeProvider>
        </Provider>
      </BrowserRouter>
    </GoogleOAuthProvider>
    </Sentry.ErrorBoundary>
  );
};

ReactDOM.createRoot(document.getElementById("root") as HTMLElement).render(
  // <React.StrictMode>
  <Root />,
  // </React.StrictMode>
);
